import React from "react";

import { AlertTriangle, Eye, EyeOff, Loader2 } from "lucide-react";

import { pendingDraftNote } from "../../Container/AccountPlanContainer/flowCopy";
import { BUCKET_STYLE } from "../../Container/AccountPlanContainer/planBucket";
import type { PlanBucket } from "../../Container/AccountPlanContainer/planBucket";
import PlanStateLabel, {
  DRAFT_HINT,
} from "../../Container/AccountPlanContainer/PlanStateLabel";
import {
  currentStep,
  forSponsor,
  hasBriefing,
} from "../../Container/AccountPlanContainer/PortfolioQuickLook";
import type {
  AccountPlanStatus,
  Persona,
  PortfolioRoles,
} from "../../Types/AccountPlan";

/**
 * Account Priorities → List: the "Account Plan" column. The eye opens the
 * same Quick look as the Account Plans tab, which leads on to the briefing; a crossed-out eye means there's nothing to read yet (no plan, or
 * no briefing generated). Add / Update / Retry take an AE to the upload step.
 */

/** What the cell shows, from the account's plan status. Exported for tests. */
export type PlanCellState =
  | "loading" // statuses not loaded yet
  | "unavailable" // the user can't open this account's plan
  | "none" // no plan uploaded
  | "processing" // first plan being read, no briefing yet
  | "failed" // latest plan couldn't be read, no briefing to fall back on
  | "needs_fixing" // latest plan was stopped by a plan check (spec VAL)
  | "needs_ok" // latest plan is waiting for the AE's OK
  | "to_confirm" // plan read, waiting on the AE's Confirm plan
  | "writing" // confirmed, the briefing is being written
  | "briefing_failed" // confirmed, the briefing couldn't be written
  | "no_briefing" // confirmed, the briefing hasn't been written yet (legacy)
  | "briefing"; // a briefing exists

export const planCellState = (
  s: AccountPlanStatus | undefined,
  loading: boolean
): PlanCellState => {
  if (!s) return loading ? "loading" : "unavailable";
  // An existing briefing stays readable while a newer version is read or fails.
  if (hasBriefing(s)) return "briefing";
  if (!s.latest_file) return "none";
  if (s.latest_file.status === "processing") return "processing";
  if (s.latest_file.status === "failed") return "failed";
  if (s.latest_file.status === "rejected") return "needs_fixing";
  if (s.latest_file.status === "needs_review") return "needs_ok";
  // F-UF-F07: only an explicit `confirmed: false` waits on the AE's confirm; a
  // confirmed version's briefing is being written, failed, or not started.
  if (s.latest_file.confirmed === false) return "to_confirm";
  if (s.sponsor_view_status === "processing") return "writing";
  if (s.sponsor_view_status === "failed") return "briefing_failed";
  return "no_briefing";
};

const rolesFor = (me: PortfolioRoles | null, id: string): Persona[] =>
  me?.admin ? ["ES", "AE", "CSM"] : me?.by_account[id] || [];

/** Only the AE uploads plans (admins hold every role). */
export const canUploadPlan = (me: PortfolioRoles | null, id: string) =>
  rolesFor(me, id).includes("AE");

export const uploadPath = (id: string) => `/account-plan/${id}?tab=plan&as=AE`;

/**
 * The persona the Quick look is shown as, as the Account Plans tab would: a
 * sponsor sees the sponsor's footer (Open briefing), AE / CSM see "Preview as
 * sponsor" and "Go to <step>". Admins see their default persona.
 */
export const quickLookPersona = (
  me: PortfolioRoles | null,
  id: string
): Persona => {
  if (me?.admin) return me.default || "AE";
  const roles = rolesFor(me, id);
  if (!roles.some(r => r !== "ES")) return "ES";
  return roles.includes("AE") ? "AE" : "CSM";
};

/** Quick look → Open briefing: the sponsor's view of the briefing. */
export const sponsorBriefingPath = (id: string) =>
  `/account-plan/${id}?tab=sponsor&as=ES`;

/** Quick look → Preview as sponsor (AE / CSM): read-only sponsor view. */
export const previewPath = (id: string) =>
  `/account-plan/${id}?tab=sponsor&preview=sponsor`;

/** Quick look → Open briefing (AE / CSM, published): the briefing, read-only. */
export const readingPath = (id: string) =>
  `/account-plan/${id}?tab=sponsor&view=briefing`;

/** Quick look → Go to <step>: the step the Account Plans tab would open. */
export const stepPath = (id: string, s?: AccountPlanStatus) =>
  `/account-plan/${id}?tab=${currentStep(s)}`;

const shortDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      })
    : null;

const briefingTooltip = (s: AccountPlanStatus) => {
  const parts = [
    s.published
      ? `Briefing published ${shortDate(s.published.at) || ""}`.trim()
      : "Draft briefing (not yet published)",
  ];
  // The plan number only (D1); an unconfirmed upload has none.
  if (s.latest_file?.plan_version != null)
    parts.push(`plan v${s.latest_file.plan_version}`);
  let text = parts.join(" · ");
  if (s.latest_file?.status === "processing")
    text += ". A newer plan is being read.";
  if (s.latest_file?.status === "failed")
    text += ". The newest upload couldn't be read.";
  if (s.latest_file?.status === "rejected")
    text += ". The newest upload wasn't used (a plan check failed).";
  if (s.latest_file?.status === "needs_review")
    text += ". The newest upload is waiting for the AE's OK.";
  return text;
};

const MUTED: Record<
  Exclude<PlanCellState, "briefing" | "loading" | "unavailable">,
  { icon: React.ElementType; label: string; tone: string; spin?: boolean }
> = {
  none: {
    icon: EyeOff,
    label: "No account plan uploaded yet",
    tone: "text-gray-300",
  },
  processing: {
    icon: Loader2, // shown as a status pill (inFlightPill), not this icon
    label: "Parsing the plan…",
    tone: "text-purple-400",
    spin: true,
  },
  failed: {
    icon: AlertTriangle,
    label: "The plan couldn't be read. Upload it again.",
    tone: "text-red-500",
  },
  needs_fixing: {
    icon: AlertTriangle,
    label: "The newest plan wasn't used: a check failed. Open it to see why.",
    tone: "text-red-500",
  },
  needs_ok: {
    icon: AlertTriangle,
    label: "The newest plan needs the AE's OK before the briefing.",
    tone: "text-amber-500",
  },
  to_confirm: {
    icon: EyeOff,
    // Confirm plan (Phase 2): the briefing is written once the AE confirms the upload.
    label: "Plan read; the briefing is written once the AE confirms it",
    tone: "text-amber-500",
  },
  writing: {
    icon: Loader2,
    label: "Plan confirmed; writing the briefing…",
    tone: "text-purple-400",
    spin: true,
  },
  briefing_failed: {
    icon: AlertTriangle,
    label:
      "Plan confirmed; the briefing couldn't be written. The AE can try again.",
    tone: "text-red-500",
  },
  no_briefing: {
    icon: EyeOff,
    label: "Plan confirmed; the briefing hasn't been written yet",
    tone: "text-amber-500",
  },
};

/**
 * A plan being parsed or briefed reads as its status, not a bare spinner (Sushant,
 * 2026-11-04): the same names and colours as the Plans list's statuses.
 */
export const inFlightPill = (
  state: PlanCellState,
  s: AccountPlanStatus | undefined
): { text: string; bucket: PlanBucket } | null => {
  if (state === "processing")
    return s?.latest_file?.queue?.state === "queued"
      ? { text: "Queued for parsing", bucket: "queued_parse" }
      : { text: "Parsing", bucket: "progress" };
  if (state === "writing")
    return s?.briefing_queue?.state === "queued"
      ? { text: "Queued for briefing", bucket: "queued_brief" }
      : { text: "Writing briefing", bucket: "progress" };
  return null;
};

const actionBtn =
  "inline-flex h-6 min-w-[56px] items-center justify-center rounded-md border px-2 text-[11px] font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

interface Props {
  accountName: string;
  status: AccountPlanStatus | undefined;
  loading: boolean;
  canUpload: boolean;
  /** Open the Quick look for this account. */
  onOpenBriefing: () => void;
  onUpload: () => void;
  /**
   * The viewer is the account's sponsor: they read the newest version, so the
   * cell labels it "Draft" or "Published <date>" (Q1 = C + A, 2026-10-19).
   */
  sponsor?: boolean;
  /**
   * Plans → All accounts (Sushant, 2026-10-29): someone who can't upload still sees
   * Add / Update, switched off, so it's clear the step is the account's AE's.
   */
  showLocked?: boolean;
}

const AccountPlanCell: React.FC<Props> = ({
  accountName,
  status: rowStatus,
  loading,
  canUpload,
  onOpenBriefing,
  onUpload,
  sponsor = false,
  showLocked = false,
}) => {
  // A sponsor's cell describes the version they read (F-44 = B).
  const status = sponsor ? forSponsor(rowStatus) : rowStatus;
  const state = planCellState(status, loading);
  const viewState =
    sponsor && status?.viewer_ready ? status.view_state || null : null;
  // The row itself toggles selection: keep clicks and keys inside this cell.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  if (state === "loading") {
    return (
      <div className="flex justify-center" aria-hidden>
        <span className="h-7 w-7 animate-pulse rounded-full bg-gray-100" />
      </div>
    );
  }
  if (state === "unavailable") {
    return (
      <div
        className="text-center text-[13px] text-gray-300"
        title="You don't have access to this account's plan"
      >
        —
      </div>
    );
  }

  const locked = !canUpload;
  // A confirmed draft isn't published yet (rule 2026-10-30): a new upload would
  // replace it, so the button says so; the upload step asks before replacing.
  const pending = rowStatus?.pending_draft || null;
  const action =
    (locked && !showLocked) || state === "processing"
      ? null
      : state === "none"
        ? "Add"
        : state === "failed" || state === "needs_fixing"
          ? "Retry"
          : state === "needs_ok"
            ? "Review"
            : pending
              ? "Replace"
              : "Update";

  return (
    <div
      className="flex flex-col items-center gap-1.5"
      onClick={stop}
      onKeyDown={stop}
      role="presentation"
    >
      {state === "briefing" && status ? (
        <button
          type="button"
          onClick={onOpenBriefing}
          title={
            viewState === "draft"
              ? `Draft briefing — ${DRAFT_HINT}`
              : briefingTooltip(status)
          }
          aria-label={`Quick look at the account plan briefing for ${accountName}`}
          className="group/eye relative inline-flex h-7 w-7 items-center justify-center rounded-full text-[#974ae4] transition hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <Eye
            className="h-[18px] w-[18px] transition-transform group-hover/eye:scale-110"
            aria-hidden
          />
          {!status.published && !viewState && (
            // Draft only: a small amber dot, explained in the tooltip.
            <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-amber-400 ring-2 ring-white" />
          )}
        </button>
      ) : (
        (() => {
          const m = MUTED[state as keyof typeof MUTED];
          const pill = inFlightPill(state, status);
          if (pill) {
            const st = BUCKET_STYLE[pill.bucket];
            return (
              <span
                role="status"
                aria-label={m.label}
                title={m.label}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.pill}`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${st.dot} ${
                    pill.bucket === "progress" ? "animate-pulse" : ""
                  }`}
                  aria-hidden
                />
                {pill.text}
              </span>
            );
          }
          const Icon = m.icon;
          return (
            <span
              role="img"
              aria-label={m.label}
              title={m.label}
              className={`inline-flex h-7 w-7 items-center justify-center ${m.tone}`}
            >
              <Icon
                className={`h-[18px] w-[18px] ${m.spin ? "animate-spin" : ""}`}
                aria-hidden
              />
            </span>
          );
        })()
      )}
      {viewState && (
        <PlanStateLabel
          state={viewState}
          publishedAt={status?.view_published_at}
        />
      )}
      {action && (
        <button
          type="button"
          onClick={onUpload}
          disabled={locked}
          aria-label={`${action} the account plan for ${accountName}`}
          title={
            locked
              ? "Only the account's AE can upload its plan"
              : action === "Add"
                ? "Upload this account's plan"
                : action === "Retry"
                  ? "Upload the plan again"
                  : action === "Replace" && pending
                    ? pendingDraftNote(pending)
                    : "Upload a newer version of the plan"
          }
          className={`${actionBtn} ${
            locked
              ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
              : action === "Add"
                ? "border-[#974ae4] bg-[#974ae4] text-white hover:bg-[#8538d4]"
                : action === "Retry"
                  ? "border-red-200 bg-white text-red-600 hover:bg-red-50"
                  : "border-gray-200 bg-white text-gray-600 hover:border-purple-300 hover:bg-purple-50 hover:text-[#8538d4]"
          }`}
        >
          {action}
        </button>
      )}
    </div>
  );
};

export default AccountPlanCell;
